import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { startsWith } from 'lodash/fp';
import React, { ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { AfterAuthRedirectManager } from '@lib/after-auth-redirect-manager';
import { useCurrentUser, usersQueryKeys } from '@portals/api/ui';
import { useAuth } from '@portals/redux';

import { useConnection } from '../hooks/websocket';

interface AuthenticationGuardProps {
  children?: ReactNode;
  redirectComponent?: ReactNode;
}

export function AuthenticationGuard({
  children,
  redirectComponent,
}: AuthenticationGuardProps) {
  const dispatch = useDispatch();

  const auth = useAuth();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const { disconnect } = useConnection();

  useEffect(
    // In some edge-cases, an already authenticated user might try accessing another account.
    // In this case, we want to reset the authentication state and remove any data related to
    // the current user - thus the "mismatch" between the auth ID & the currentUser ID.
    function resetAuthenticationOnMismatch() {
      const isStaleUser = !auth && currentUser.data;
      const isMismatchBetweenAuthAndUser =
        auth && currentUser.data && auth.id !== currentUser.data.id;

      if (isStaleUser || isMismatchBetweenAuthAndUser) {
        queryClient.removeQueries({
          queryKey: usersQueryKeys.all(),
        });
      }
    },
    [currentUser.data, auth, queryClient, dispatch]
  );

  if (auth) {
    const scope = Sentry.getCurrentScope();
    scope.setUser({ id: auth.email });

    if (children) {
      return <>{children}</>;
    }

    return <Outlet />;
  } else {
    queryClient.clear();
    disconnect();

    if (redirectComponent) {
      return <>{redirectComponent}</>;
    }

    return <AuthenticationRedirect />;
  }
}

function AuthenticationRedirect() {
  const { pathname, search } = useLocation();

  // Save prev path and search, so we can redirect back after login
  if (pathname && !startsWith('?org=', search)) {
    AfterAuthRedirectManager.setPathname(pathname + search);
  }

  return <Navigate to="/auth/sign-in" replace />;
}
